import Miniservices from "../components/Miniservices";
import { useI18n } from "../i18n";

const Services = () => {
  const { services } = useI18n();

  return (
    <main>
      <section className="services-hero relative isolate overflow-hidden bg-slate-50 px-6 pb-16 pt-36 text-slate-900 md:px-12 md:pb-24 md:pt-44">
        {/* Animated abstract background */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
        >
          {/* First gradient */}
          <div className="services-glow services-glow-one absolute -right-32 -top-40 h-[28rem] w-[28rem] rounded-full bg-sky-300/40 blur-[110px]" />

          {/* Second gradient */}
          <div className="services-glow services-glow-two absolute -bottom-48 -left-32 h-[30rem] w-[30rem] rounded-full bg-blue-200/50 blur-[120px]" />

          {/* Third gradient */}
          <div className="services-glow services-glow-three absolute left-[40%] top-[20%] h-64 w-64 rounded-full bg-cyan-100/60 blur-[100px]" />

          {/* Abstract grid */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #334155 1px, transparent 1px), linear-gradient(to bottom, #334155 1px, transparent 1px)",
              backgroundSize: "55px 55px",
            }}
          />

          {/* Abstract lines */}
          <svg
            className="absolute inset-0 h-full w-full opacity-40"
            viewBox="0 0 1440 700"
            preserveAspectRatio="xMidYMid slice"
            fill="none"
          >
            <path
              d="M-100 500C200 300 350 650 650 420S1100 200 1540 400"
              stroke="url(#servicesLineGradient)"
              strokeWidth="1.5"
            />

            <path
              d="M-100 530C200 330 350 680 650 450S1100 230 1540 430"
              stroke="url(#servicesLineGradient)"
              strokeWidth="1"
            />

            <path
              d="M800 -100C900 100 1100 150 1300 50S1500 150 1600 250"
              stroke="url(#servicesLineGradient)"
              strokeWidth="1.5"
            />

            <circle
              cx="1150"
              cy="180"
              r="100"
              stroke="#38BDF8"
              strokeOpacity="0.25"
            />

            <circle
              cx="1150"
              cy="180"
              r="125"
              stroke="#38BDF8"
              strokeOpacity="0.15"
            />

            <defs>
              <linearGradient
                id="servicesLineGradient"
                x1="0"
                y1="0"
                x2="1440"
                y2="700"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#38BDF8" stopOpacity="0" />
                <stop offset="0.5" stopColor="#38BDF8" stopOpacity="0.6" />
                <stop offset="1" stopColor="#6366F1" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>

          {/* Soft overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white/40" />
        </div>

        {/* Content */}
        <div className="relative mx-auto max-w-7xl">
          {/* Eyebrow */}
          <p className="services-animate services-animate-one mb-5 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-sky-700 sm:text-sm">
            <span className="services-accent-line h-px w-8 bg-sky-600" />
            Seaway Logistics
          </p>

          {/* Title */}
          <h1 className="services-animate services-animate-two max-w-4xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl md:text-7xl">
            {services.title}
          </h1>

          {/* Description */}
          <p className="services-animate services-animate-three mt-6 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
            {services.pageIntro}
          </p>

          {/* Bottom accent */}
          <div className="services-bottom-line mt-8 h-1 w-24 rounded-full bg-gradient-to-r from-sky-500 to-blue-400" />
        </div>
      </section>

      <Miniservices showTitle={false} />

      {/* CSS animations */}
      <style>{`
        .services-animate {
          opacity: 0;
          transform: translateY(28px);
          animation: servicesFadeUp 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        .services-animate-one {
          animation-delay: 0.1s;
        }

        .services-animate-two {
          animation-delay: 0.25s;
        }

        .services-animate-three {
          animation-delay: 0.4s;
        }

        @keyframes servicesFadeUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .services-glow {
          will-change: transform;
        }

        .services-glow-one {
          animation: servicesGlowOne 14s ease-in-out infinite;
        }

        .services-glow-two {
          animation: servicesGlowTwo 18s ease-in-out infinite;
        }

        .services-glow-three {
          animation: servicesGlowThree 16s ease-in-out infinite;
        }

        @keyframes servicesGlowOne {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(-35px, 30px) scale(1.12);
          }
        }

        @keyframes servicesGlowTwo {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(30px, -25px) scale(1.1);
          }
        }

        @keyframes servicesGlowThree {
          0%, 100% {
            transform: translate(0, 0);
          }
          50% {
            transform: translate(25px, 35px);
          }
        }

        .services-accent-line {
          transform-origin: left;
          animation: servicesLineReveal 0.7s ease-out 0.3s both;
        }

        .services-bottom-line {
          transform: scaleX(0);
          transform-origin: left;
          animation: servicesLineReveal 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.65s forwards;
        }

        @keyframes servicesLineReveal {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .services-animate,
          .services-glow,
          .services-accent-line,
          .services-bottom-line {
            animation: none !important;
            opacity: 1;
            transform: none;
          }
        }
      `}</style>
    </main>
  );
};

export default Services;